import { observer } from "mobx-react-lite";
import { DynamicHorizontalMobXStepper, DynamicVerticalMobXStepper, MobXStepperState } from "../../common/stepper/MobXStepperComponents";
import { Box, Button, Card, CardContent, Dialog, DialogContent, Divider, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useAppServices } from "../app/services";
import React from "react";
import { Alert } from "@mui/material";
import { DialogState, useShouldDialogFullScreen } from "../core/dialog/GlobalDialogState";
import { DialogTopBar } from "../core/dialog/DialogComponents";
import Grid from "@mui/material/Grid2";
import { ClipboardButton, ClipboardText } from "../../common/clipboard/ClipboardComponents";
import { TwoFactorAuthState } from "./AuthService";
import { renderServerDataWithLoadingBox, useInitData } from "../core/data/DataLoaderHooks";
import { StepConfig } from "../../common/stepper/StepperHelpers";
import { useGetMyUserInfo } from "../user/user_hooks";
import { useMountEffect } from "../../common/hooks/hookslib";
import { useTranslation } from "react-i18next";
import { appI18n } from "../i18n/i18n";
import type { TFunction } from "i18next";

const use2FAStyles = () => {
    const t = useTheme();
    return {
        keyCard: {
            background: t.palette.background.default,
            width: "100%",
        },
    };
};

// ======================
// TwoFactorAuthDialog
// ======================

interface TwoFactorAuthDialogProps {
    dialogState: DialogState;
}

export const TwoFactorAuthDialog: React.FC<TwoFactorAuthDialogProps> = observer((p) => {
    const { authService } = useAppServices();

    useMountEffect(() => {
        authService.initTwoFactorAuthState();
    });

    if (!!authService.twoFactorAuthState) {
        return <TwoFactorAuthDialogContent state={authService.twoFactorAuthState} dialogState={p.dialogState} />;
    }
    return null;
});

// ======================
// TwoFactorAuthDialogContent
// ======================

interface TwoFactorAuthDialogContentProps {
    state: TwoFactorAuthState;
    dialogState: DialogState;
}

export const TwoFactorAuthDialogContent: React.FC<TwoFactorAuthDialogContentProps> = observer((p) => {
    const fullScreen = useShouldDialogFullScreen();
    const stepperState = p.state.enable2FAStepperState;
    const getMyUserInfo = useGetMyUserInfo();
    const { t } = useTranslation();
    const onClickNext = async () => {
        if (!!stepperState.stepConfigs[stepperState.activeStep].onClickNext) {
            await stepperState.stepConfigs[stepperState.activeStep].onClickNext();
        }
        stepperState.goToNextStep();
    };

    const onFinish = async () => {
        p.dialogState.close();
        await getMyUserInfo.refetch();
    };

    return (
        <Dialog open={p.dialogState.isOpen} onClose={p.dialogState.close} maxWidth={"md"} fullWidth fullScreen={fullScreen}>
            <DialogTopBar dialogState={p.dialogState} title={t("auth:twoFactor.dialogTitle")} />
            <EnableTwoFactorAuthStepper stepperState={stepperState} twoFactorAuthState={p.state} />
            <Divider />
            <Box display={"flex"} justifyContent={"space-between"} p={2}>
                <Button
                    variant={"outlined"}
                    disabled={!stepperState.hasPreviousStep}
                    onClick={() => {
                        stepperState.goBackOneStep();
                    }}
                >
                    {t("common:actions.back")}
                </Button>
                {!stepperState.isLastStep && (
                    <Button variant={"contained"} color={"primary"} onClick={onClickNext}>
                        {t("common:actions.next")}
                    </Button>
                )}
                {stepperState.isLastStep && (
                    <Button variant={"contained"} color={"primary"} onClick={onFinish}>
                        {t("common:actions.finish")}
                    </Button>
                )}
            </Box>
        </Dialog>
    );
});

// ======================
// EnableTwoFactorAuthStepper
// ======================

interface EnableTwoFactorAuthStepperProps {
    stepperState: MobXStepperState;
    twoFactorAuthState: TwoFactorAuthState;
}

export const EnableTwoFactorAuthStepper: React.FC<EnableTwoFactorAuthStepperProps> = observer((p) => {
    const fullScreen = useShouldDialogFullScreen();
    const { t } = useTranslation();
    return (
        <DialogContent>
            {!fullScreen && (
                <Box minHeight={400}>
                    <DynamicVerticalMobXStepper stepConfigs={generate2FaStepConfigs(p.twoFactorAuthState, t)} stepperState={p.stepperState} />
                </Box>
            )}
            {fullScreen && <DynamicHorizontalMobXStepper stepConfigs={generate2FaStepConfigs(p.twoFactorAuthState, t)} stepperState={p.stepperState} />}
        </DialogContent>
    );
});

// ======================
// Prepare2FAStep
// ======================

interface Prepare2FAStepProps {}

export const Prepare2FAStep: React.FC<Prepare2FAStepProps> = observer((p) => {
    const { t } = useTranslation();
    return (
        <>
            <Box>
                <Typography variant={"h5"}>{t("auth:twoFactor.prepare.title")}</Typography>
                <Typography variant={"body1"}>{t("auth:twoFactor.prepare.description")}</Typography>
                <br />
                <Alert severity={"info"}>{t("auth:twoFactor.prepare.lostPhoneWarning")}</Alert>
            </Box>
        </>
    );
});

// ======================
// Scan2FACodeStep
// ======================

interface Scan2FACodeStepProps {
    state: TwoFactorAuthState;
}

export const Scan2FACodeStep: React.FC<Scan2FACodeStepProps> = observer((p) => {
    const { state } = p;

    const styles = use2FAStyles();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const { t } = useTranslation();

    const keyId = "authenticationKeyId";

    useInitData({
        init: () => state.twoFACode.fetchData(),
    });

    return (
        <>
            <Box>
                <Typography variant={"h5"}>{t("auth:twoFactor.scan.title")}</Typography>
                <Typography variant={"body1"}>{t("auth:twoFactor.scan.description")}</Typography>
                {renderServerDataWithLoadingBox(state.twoFACode, (data) => {
                    return (
                        <Box pt={2}>
                            <Grid container spacing={2} justifyContent={"center"}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 3,
                                    }}
                                >
                                    <Box width={"100%"} display={"flex"} justifyContent={"center"}>
                                        <img
                                            src={`data:image/jpg;base64, ${data.getImage_asB64()}`}
                                            alt={t("auth:twoFactor.scan.qrAlt")}
                                            width={isMobile ? 200 : 150}
                                            height={isMobile ? 200 : 150}
                                        />
                                    </Box>
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 9,
                                    }}
                                >
                                    <Card sx={styles.keyCard}>
                                        <CardContent>
                                            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                                                <ClipboardText clipboardId={keyId}>{data.getSecret()}</ClipboardText>
                                                <ClipboardButton clipboardId={keyId} iconButton />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    );
                })}
            </Box>
        </>
    );
});

// ======================
// Verify2FACodeStep
// ======================

interface Verify2FACodeStepProps {
    state: TwoFactorAuthState;
}

export const Verify2FACodeStep: React.FC<Verify2FACodeStepProps> = observer((p) => {
    const { state } = p;
    const { t } = useTranslation();

    return (
        <>
            <Box>
                <Typography variant={"h5"}>{t("auth:twoFactor.verify.title")}</Typography>
                <Typography variant={"body1"}>{t("auth:twoFactor.verify.description")}</Typography>
                <br />
                <TextField
                    variant={"filled"}
                    value={state.verificationCode}
                    id={"verifyToken"}
                    label={t("auth:twoFactor.verify.token")}
                    onChange={(e) => {
                        state.setVerificationCode(e.target.value);
                    }}
                    fullWidth
                    helperText={t("auth:twoFactor.verify.help")}
                    required
                />
            </Box>
        </>
    );
});

// ======================
// RecoveryCodeStep
// ======================

interface RecoveryCodeStepProps {
    state: TwoFactorAuthState;
}

export const RecoveryCodeStep: React.FC<RecoveryCodeStepProps> = observer((p) => {
    const { t } = useTranslation();
    return (
        <>
            <Box>
                <Typography variant={"h5"}>{t("auth:twoFactor.recovery.title")}</Typography>
                <Typography variant={"body1"}>{t("auth:twoFactor.recovery.description")}</Typography>
                <br />
                <RecoveryCodeList state={p.state} />
            </Box>
        </>
    );
});

// ======================
// RecoveryCodeList
// ======================

interface RecoveryCodeListProps {
    state: TwoFactorAuthState;
}

export const RecoveryCodeList: React.FC<RecoveryCodeListProps> = observer((p) => {
    const { state } = p;

    const styles = use2FAStyles();

    const codesId = "recoveryCodesId";

    useInitData({
        poll: () => state.recoveryCodes.fetchData(),
        pollInterval: 30,
    });
    return renderServerDataWithLoadingBox(state.recoveryCodes, (data) => {
        return (
            <Box pt={2}>
                <Card sx={styles.keyCard}>
                    <CardContent>
                        <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                            <ClipboardText clipboardId={codesId}>
                                <ul>
                                    {data.getRecoveryCodesList().map((c, i) => (
                                        <li key={i}>{c}</li>
                                    ))}
                                </ul>
                            </ClipboardText>
                            <ClipboardButton clipboardId={codesId} iconButton />
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        );
    });
});

export const generate2FaStepConfigs = (state?: TwoFactorAuthState, t: TFunction = appI18n.t): StepConfig[] => [
    {
        id: "prepare",
        label: t("auth:twoFactor.steps.prepare"),
        renderer: () => <Prepare2FAStep />,
    },
    {
        id: "scan",
        label: t("auth:twoFactor.steps.scan"),
        renderer: () => <Scan2FACodeStep state={state} />,
    },
    {
        id: "verify",
        label: t("auth:twoFactor.steps.verify"),
        renderer: () => <Verify2FACodeStep state={state} />,
        onClickNext: async () => await state.enable2Fa(),
    },
    {
        id: "copyRecoveryCodes",
        label: t("auth:twoFactor.steps.copyRecoveryCodes"),
        renderer: () => <RecoveryCodeStep state={state} />,
    },
];
